import ShareIcon from '@mui/icons-material/Share';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import React, { useState } from 'react';

import {
  downloadCsv,
  generateAndDownloadPdf,
  generateCsvData,
} from '../_utils/reportExportHelper';

/**
 * A reusable React component for a share/export button with a dropdown menu,
 * using Material-UI components and Tailwind CSS for styling.
 *
 * @param {Array<Object>} data The report data to be exported.
 * @param {string} fileName The base file name for the export.
 * @param {string} title The title for the PDF report.
 * @param {boolean} [iconOnly=false] If true, the button will only show the icon.
 * @param {object} [sx={}] sx props for component.
 */
function ShareButton({ data, fileName, title, iconOnly = false, sx = {} }) {
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  // Handles the CSV export process
  const handleCsvExport = async () => {
    setLoading(true);
    const csvContent = generateCsvData(data);
    downloadCsv(csvContent, `${fileName}.csv`);
    setAnchorEl(null);
    setLoading(false);
  };

  // Handles the PDF export process
  const handlePdfExport = async () => {
    setLoading(true);
    generateAndDownloadPdf(data, title, `${fileName}.pdf`);
    setAnchorEl(null);
    setLoading(false);
  };

  // Handle opening the menu
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle closing the menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box className='relative inline-block text-left' sx={sx}>
      {iconOnly ? (
        <IconButton
          aria-label='share-report-data'
          onClick={handleMenuOpen}
          color='inherit'
        >
          {loading ? <CircularProgress /> : <ShareIcon />}
        </IconButton>
      ) : (
        <Button
          size='small'
          aria-label='share-report-data'
          onClick={handleMenuOpen}
          variant='outlined'
          color='inherit'
          startIcon={loading ? <CircularProgress /> : <ShareIcon />}
          // className='rounded-md bg-cyan-600 shadow-lg hover:bg-cyan-700'
          sx={{
            minWidth: iconOnly ? '48px' : 'auto',
            padding: '8px 16px',
            ...(iconOnly && { padding: '8px' }),
          }}
        >
          Share Report
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem onClick={handleCsvExport}>Export as CSV</MenuItem>
        <MenuItem onClick={handlePdfExport}>Export as PDF</MenuItem>
      </Menu>
    </Box>
  );
}

export default ShareButton;
